import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';

import Container from '../../../components/Container';
import Loader from '../../../components/Loader';
import AlertMsg from '../../../components/AlertMsg';
import AppDialog from '../../../components/AppDialog';
import ShadcnTabs, { ShadcnTabPanel } from '../../../components/ShadcnTabs';

import {
  ProvidersList,
  ProvidersClientSummary,
  ProviderEditDialog,
  ProviderCreateDialog,
} from '../components';
import {
  useGetPaymentProviders,
  useUpsertProviderGlobalFields,
  useToggleProviderEnabled,
  useCreatePaymentProvider,
  useDeleteProvider,
} from '../services';
import { TPaymentTerminalProvider, TCreateProviderRequest } from '../schemas/payment-provider-schema';

const MASK = '••••••••';

const PaymentProvidersPage = () => {
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<'global' | 'client'>('global');
  const [editingProviderId, setEditingProviderId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<TPaymentTerminalProvider | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [msgAlert, setMsgAlert] = useState({ open: false, message: '', msgType: 'error' });

  const { data: providers = [], isLoading, refetch } = useGetPaymentProviders();
  const upsertMutation = useUpsertProviderGlobalFields();
  const toggleMutation = useToggleProviderEnabled();
  const createMutation = useCreatePaymentProvider();
  const deleteMutation = useDeleteProvider();

  const handleEdit = (provider: TPaymentTerminalProvider) => {
    setEditingProviderId(provider.providerId);
  };

  const handleCloseEdit = () => {
    setEditingProviderId(null);
  };

  const handleSaveGlobalFields = async (globalFields: Record<string, string>) => {
    if (!editingProviderId) return;
    const current = queryClient.getQueryData<TPaymentTerminalProvider>([
      'payment-terminal-providers',
      editingProviderId,
    ]);
    const encryptedKeys = current?.globalFieldsEncryptedKeys || [];

    const payload: Record<string, string> = {};
    Object.entries(globalFields).forEach(([key, value]) => {
      if (encryptedKeys.includes(key) && value === MASK) return;
      if (encryptedKeys.includes(key) && value === '') return;
      payload[key] = value;
    });

    try {
      await upsertMutation.mutateAsync({
        providerId: editingProviderId,
        payload: { globalFields: payload },
      });
      setMsgAlert({ open: true, message: 'Provider updated successfully', msgType: 'success' });
      refetch();
    } catch (err: any) {
      setMsgAlert({
        open: true,
        message: err?.response?.data?.msg || 'Failed to update provider',
        msgType: 'error',
      });
    }
  };

  const handleToggleEnabled = async (provider: TPaymentTerminalProvider) => {
    setTogglingId(provider.providerId);
    try {
      await toggleMutation.mutateAsync({
        providerId: provider.providerId,
        payload: { isEnabled: !provider.isEnabled },
      });
      setMsgAlert({
        open: true,
        message: `Provider ${!provider.isEnabled ? 'enabled' : 'disabled'}`,
        msgType: 'success',
      });
      refetch();
    } catch (err: any) {
      setMsgAlert({
        open: true,
        message: err?.response?.data?.msg || 'Failed to toggle provider',
        msgType: 'error',
      });
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreate = async (payload: TCreateProviderRequest) => {
    try {
      await createMutation.mutateAsync(payload);
      setMsgAlert({ open: true, message: 'Provider created', msgType: 'success' });
      setCreateOpen(false);
      refetch();
    } catch (err: any) {
      setMsgAlert({
        open: true,
        message: err?.response?.data?.msg || 'Failed to create provider',
        msgType: 'error',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!providerToDelete) return;
    try {
      await deleteMutation.mutateAsync(providerToDelete.providerId);
      setMsgAlert({ open: true, message: 'Provider deleted', msgType: 'success' });
      setProviderToDelete(null);
      refetch();
    } catch (err: any) {
      setMsgAlert({
        open: true,
        message: err?.response?.data?.msg || 'Failed to delete provider',
        msgType: 'error',
      });
    }
  };

  const clientCount = providers.filter((p) =>
    (p.schema?.fields || []).some((f) => f.scope === 'client'),
  ).length;

  return (
    <Container page="payment_providers" searchVal="" onSearch={() => null} searchLabel="">
      <Grid container item xs={12}>
        <Grid item xs={12} mb={2}>
          <Box sx={styles.header}>
            <Typography sx={styles.title}>Payment Providers</Typography>
            <Typography sx={styles.subtitle}>
              Manage the global catalog of kiosk/POS payment terminal providers.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sx={styles.mainView} px={3} pt={2} pb={3}>
          {isLoading ? (
            <Box sx={styles.loaderContainer}>
              <Loader />
            </Box>
          ) : (
            <>
              <Box sx={styles.headerRow} mb={2}>
                <ShadcnTabs
                  value={tab}
                  onChange={(v) => setTab(v as 'global' | 'client')}
                  tabs={[
                    { value: 'global', label: 'Global', count: providers.length },
                    { value: 'client', label: 'Client', count: clientCount },
                  ]}
                />
                <Button
                  sx={styles.addBtn}
                  onClick={() => setCreateOpen(true)}
                  startIcon={<AddIcon />}
                >
                  Register Provider
                </Button>
              </Box>

              <ShadcnTabPanel value="global" current={tab}>
                <Typography sx={styles.blurb}>
                  Foodo&apos;s catalog of payment terminal providers. Set the shared globals, toggle
                  availability and manage field schemas here.
                </Typography>
                <ProvidersList
                  providers={providers}
                  onEdit={handleEdit}
                  onDelete={(p) => setProviderToDelete(p)}
                  onToggleEnabled={handleToggleEnabled}
                  togglingId={togglingId}
                />
              </ShadcnTabPanel>

              <ShadcnTabPanel value="client" current={tab}>
                <Typography sx={styles.blurb}>
                  Client-scope fields each restaurant must fill in per provider. Click a row to
                  manage the schema in the provider editor.
                </Typography>
                <ProvidersClientSummary providers={providers} onEdit={handleEdit} />
              </ShadcnTabPanel>
            </>
          )}
        </Grid>
      </Grid>

      <ProviderEditDialog
        open={!!editingProviderId}
        providerId={editingProviderId}
        onClose={handleCloseEdit}
        onSave={handleSaveGlobalFields}
        isSaving={upsertMutation.isPending}
      />

      <ProviderCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreate}
        isSaving={createMutation.isPending}
      />

      <AppDialog
        open={!!providerToDelete}
        onClose={() => setProviderToDelete(null)}
        title="Delete Provider?"
        content={`This permanently removes ${providerToDelete?.displayName || providerToDelete?.providerId} from the catalog. Refused if any restaurant has a config row for it — clear those first.`}
        firstBtnContent="Cancel"
        secondBtnContent="Delete"
        firstBtnClickHandler={() => setProviderToDelete(null)}
        secondBtnClickHandler={handleDeleteConfirm}
        options={{ keepMounted: false, btnLoading: deleteMutation.isPending }}
      />

      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
      />
    </Container>
  );
};

export default PaymentProvidersPage;

const styles = {
  header: {
    padding: 2,
  },
  title: {
    fontFamily: 'GilroyBold',
    fontSize: 28,
    color: '#1a1a1a',
    marginBottom: 0.5,
  },
  subtitle: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: '#666',
  },
  mainView: {
    backgroundColor: '#FFF',
    borderRadius: '8px',
    minHeight: 'calc(100vh - 200px)',
  },
  loaderContainer: {
    width: '100%',
    minHeight: 'calc(100vh - 300px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
  },
  blurb: {
    fontFamily: 'GilroyMedium',
    fontSize: 12,
    color: '#64748b',
    mb: 2,
  },
  addBtn: {
    backgroundColor: '#000',
    color: '#FFF',
    fontSize: 14,
    borderRadius: '8px',
    textTransform: 'capitalize' as const,
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    paddingTop: '8px',
    paddingBottom: '8px',
    fontFamily: 'GilroySemiBold',
    '&:hover': { backgroundColor: '#333' },
    '&:disabled': { opacity: 0.5, color: '#FFF' },
  },
};
